'use client'

import posthog from 'posthog-js'
import { useRef, useState } from 'react'
import type { TurnstileHandle } from '@/components/turnstile-widget'

export type NewsletterStatus = 'idle' | 'submitting' | 'success' | 'error'

export type NewsletterPlacement = 'footer' | 'article' | 'article-mid'

// Shared with the catalog-blog `/api/newsletter` route — must stay in
// sync with the inlined honeypot field name there. The frontend never
// fills it; bots that auto-fill every named input will trip the gate.
export const NEWSLETTER_HONEYPOT_FIELD = 'website'

export interface UseNewsletterSubscribeOptions {
	/** Where the form is rendered — tags the success analytics event. */
	placement: NewsletterPlacement
	/** Attribution tag sent to the API route, e.g. 'blog' or 'blog-article'. */
	source?: string
}

export interface UseNewsletterSubscribeResult {
	email: string
	setEmail: (email: string) => void
	honey: string
	setHoney: (honey: string) => void
	status: NewsletterStatus
	errorMsg: string
	turnstileRef: React.RefObject<TurnstileHandle | null>
	handleSubmit: (e: React.FormEvent) => Promise<void>
}

/**
 * Shared newsletter-signup submission logic: POST to `/api/newsletter`,
 * Turnstile token handling, honeypot field, loading/success/error state,
 * and the PostHog capture call on success. Reused by the footer form and
 * the in-article CTA so the fetch/Turnstile/honeypot logic lives in one
 * place — only the presentational wrapper differs per placement.
 */
export function useNewsletterSubscribe({
	placement,
	source = 'blog',
}: UseNewsletterSubscribeOptions): UseNewsletterSubscribeResult {
	const [email, setEmail] = useState('')
	const [honey, setHoney] = useState('')
	const [status, setStatus] = useState<NewsletterStatus>('idle')
	const [errorMsg, setErrorMsg] = useState('')
	const turnstileRef = useRef<TurnstileHandle>(null)

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!email.trim()) return

		setStatus('submitting')
		setErrorMsg('')

		try {
			// Run the (invisible) Turnstile challenge on submit; null when
			// Turnstile is unavailable — the API route is fail-open.
			const turnstileToken = (await turnstileRef.current?.execute()) ?? null
			const res = await fetch('/api/newsletter', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					email: email.trim(),
					source,
					turnstileToken,
					[NEWSLETTER_HONEYPOT_FIELD]: honey,
				}),
			})

			const data = await res.json()

			if (!res.ok) {
				setStatus('error')
				setErrorMsg(data.error || 'Something went wrong.')
				return
			}

			posthog.capture('email_subscribed', { source, placement })
			setStatus('success')
			setEmail('')
		} catch {
			setStatus('error')
			setErrorMsg('Network error. Please try again.')
		}
	}

	return { email, setEmail, honey, setHoney, status, errorMsg, turnstileRef, handleSubmit }
}
